import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TaskChooseOrganization, useAuth, useOrganizationList, useUser } from "@clerk/clerk-react";
import { User, Users, ArrowLeft, Loader2 } from "lucide-react";
import { setWorkspaceType } from "../../lib/api";

const PRIMARY = "#018D4C";
const DONE_URL = "/dashboard/overview";

// Clerk sends pending sessions here (taskUrls 'choose-organization'). Every account is an
// org: "Just me" creates a one-member org, "My team" creates one and invites teammates.
export default function WorkspacePage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { getToken } = useAuth({ treatPendingAsSignedOut: false });
  const { isLoaded, createOrganization, setActive, userMemberships, userInvitations } = useOrganizationList({
    userMemberships: true,
    userInvitations: true,
  });
  const [mode, setMode] = useState(null); // null | "team" | "join"
  const [companyName, setCompanyName] = useState("");
  const [invites, setInvites] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const canJoin = (userMemberships?.count || 0) + (userInvitations?.count || 0) > 0;

  async function setUp(accountType, name, emails = []) {
    setBusy(true);
    setError("");
    try {
      const org = await createOrganization({ name });
      await setActive({ organization: org.id });
      const token = await getToken();
      await setWorkspaceType(accountType, token);
      if (emails.length) {
        await org.inviteMembers({ emailAddresses: emails, role: "org:member" });
      }
      navigate(DONE_URL, { replace: true });
    } catch (e) {
      setError(e?.errors?.[0]?.longMessage || e?.response?.data?.detail || e?.message || "Something went wrong.");
      setBusy(false);
    }
  }

  function startPersonal() {
    const first = (user?.firstName || "").trim();
    setUp("personal", first ? `${first}'s workspace` : "Personal workspace");
  }

  function startTeam(e) {
    e.preventDefault();
    const emails = invites.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    setUp("team", companyName.trim(), emails);
  }

  if (!isLoaded) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] px-4">
      <div className="w-full max-w-xl">
        {mode === "join" ? (
          <>
            <BackButton onClick={() => setMode(null)} />
            <TaskChooseOrganization redirectUrlComplete={DONE_URL} />
          </>
        ) : mode === "team" ? (
          <form onSubmit={startTeam} className="bg-white border border-zinc-100 shadow-lg rounded-xl p-8 space-y-5">
            <BackButton onClick={() => setMode(null)} />
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">Set up your team</h1>
              <p className="text-sm text-zinc-500 mt-1">Your agency or company name. You can invite teammates now or later.</p>
            </div>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold text-zinc-700">Team name</span>
              <input
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme SEO"
                className="w-full px-3 py-2.5 text-sm border border-zinc-200 bg-white focus:outline-none focus:ring-2 rounded-lg"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold text-zinc-700">Invite teammates (optional)</span>
              <textarea
                rows={3}
                value={invites}
                onChange={(e) => setInvites(e.target.value)}
                placeholder="anna@acme.com, ben@acme.com"
                className="w-full px-3 py-2.5 text-sm border border-zinc-200 bg-white focus:outline-none focus:ring-2 rounded-lg resize-none"
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={busy || !companyName.trim()}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-sm font-bold text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: PRIMARY }}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Create team workspace
            </button>
          </form>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-zinc-900">How will you use WhyRank?</h1>
              <p className="text-sm text-zinc-500 mt-2">This sets up your workspace. You can add teammates later.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <ChoiceCard
                icon={User}
                title="Just me"
                body="One workspace for your own site."
                disabled={busy}
                onClick={startPersonal}
              />
              <ChoiceCard
                icon={Users}
                title="My team"
                body="Shared workspace for an agency or content team."
                disabled={busy}
                onClick={() => setMode("team")}
              />
            </div>
            {busy && (
              <p className="flex items-center justify-center gap-2 text-sm text-zinc-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Creating your workspace…
              </p>
            )}
            {error && <p className="text-sm text-red-600 text-center">{error}</p>}
            {canJoin && (
              <p className="text-center text-sm text-zinc-500">
                Invited to a team?{" "}
                <button onClick={() => setMode("join")} className="font-medium underline" style={{ color: PRIMARY }}>
                  Join an existing workspace
                </button>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ChoiceCard({ icon: Icon, title, body, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-left bg-white border border-zinc-100 shadow-sm hover:shadow-md hover:border-[#018D4C]/40 rounded-xl p-6 transition disabled:opacity-50"
    >
      <Icon className="w-6 h-6" style={{ color: PRIMARY }} />
      <p className="mt-3 text-lg font-bold text-zinc-900">{title}</p>
      <p className="text-sm text-zinc-500 mt-1">{body}</p>
    </button>
  );
}

function BackButton({ onClick }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1 text-xs font-bold text-zinc-500 hover:text-zinc-900 mb-3">
      <ArrowLeft className="w-3.5 h-3.5" /> Back
    </button>
  );
}
